/**
 * Live System Status — queries /api/health/services and renders the actual state.
 * Replaces the previous static placeholder.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, XCircle, Wrench, ArrowLeft, Loader2, Calendar, AlertOctagon } from "lucide-react";

const TONE = {
  operational: { c: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2, label: "Operational" },
  degraded:    { c: "text-amber-700 bg-amber-50 border-amber-200",         icon: AlertTriangle, label: "Degraded" },
  outage:      { c: "text-rose-700 bg-rose-50 border-rose-200",             icon: XCircle, label: "Outage" },
  maintenance: { c: "text-sky-700 bg-sky-50 border-sky-200",                icon: Wrench, label: "Maintenance" },
};

export default function SystemStatus() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState([]);
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/health/services").then((r) => r.data).catch(() => ({ overall: "outage", checks: [], generated_at: new Date().toISOString() })),
      api.get("/governance/maintenance").then((r) => r.data.items || []).catch(() => []),
      api.get("/governance/incidents").then((r) => r.data.items || []).catch(() => []),
    ]).then(([svc, mnt, inc]) => {
      if (cancel) return;
      setData(svc);
      setMaintenance(mnt);
      setIncidents(inc);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const activeIncidents = incidents.filter((i) => i.state !== "resolved");
  const upcomingMaintenance = maintenance.filter((m) => m.status !== "completed");

  const overall = data?.overall || "operational";
  const OverallTone = TONE[overall] || TONE.operational;
  const OverallIcon = OverallTone.icon;

  return (
    <div className="max-w-3xl space-y-6" data-testid="system-status">
      <div>
        <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => navigate("/help")}>
          <ArrowLeft className="h-3 w-3" /> Back to Help
        </button>
        <div className="mt-3 text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">System status · live</div>
        <div className="mt-1 flex items-center gap-3">
          <OverallIcon className={`h-6 w-6 ${OverallTone.c.split(" ")[0]}`} strokeWidth={1.6} />
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">
            {loading ? "Checking…" : overall === "operational" ? "All systems operational" : `Status: ${OverallTone.label}`}
          </h1>
        </div>
        {data?.generated_at && (
          <div className="mt-2 text-xs text-slate-500">Refreshed {new Date(data.generated_at).toLocaleString("en-GB")}</div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Probing services…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {(data?.checks || []).map((c) => {
            const tone = TONE[c.status] || TONE.operational;
            const Icon = tone.icon;
            return (
              <div key={c.component} className="px-4 py-3 flex items-start gap-3" data-testid={`sys-${c.component.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${tone.c.split(" ")[0]}`} strokeWidth={1.8} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 font-medium">{c.component}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.detail}</div>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 border ${tone.c}`}>{tone.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {activeIncidents.length > 0 && (
        <section className="space-y-2" data-testid="status-incidents-active">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-rose-600" />
            <h2 className="text-lg font-heading font-semibold text-slate-900">Active incidents</h2>
          </div>
          <div className="bg-white border border-slate-200 rounded-md divide-y">
            {activeIncidents.map((i) => (
              <div key={i.id} className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-rose-200 text-rose-700 bg-rose-50">{i.severity}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-200 text-amber-700 bg-amber-50">{i.state}</span>
                  <span className="text-sm font-semibold text-slate-900">{i.title}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Started {new Date(i.started_at).toLocaleString("en-GB")}</div>
                {(i.updates || []).length > 0 && (
                  <div className="mt-2 text-xs text-slate-700">{i.updates[i.updates.length - 1].note}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {upcomingMaintenance.length > 0 && (
        <section className="space-y-2" data-testid="status-maintenance">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            <h2 className="text-lg font-heading font-semibold text-slate-900">Scheduled maintenance</h2>
          </div>
          <div className="bg-white border border-slate-200 rounded-md divide-y">
            {upcomingMaintenance.map((m) => (
              <div key={m.id} className="p-4">
                <div className="text-sm font-semibold text-slate-900">{m.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{m.starts_at} → {m.ends_at} ({m.timezone})</div>
                <div className="text-xs text-slate-600 mt-1">Impact: <span className="font-medium">{m.expected_impact}</span>{m.affected_services?.length ? ` · ${m.affected_services.join(", ")}` : ""}</div>
                {m.notes && <div className="text-xs text-slate-500 mt-1">{m.notes}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed">
        Status is driven by live server-side probes. Full incident history is available in the <Link to="/notices" className="text-slate-900 underline underline-offset-2">notices feed</Link>.
      </p>
    </div>
  );
}
